/**
 * Sheets schema constants.
 *
 * Mirrors design.md "Sheets schema 採單一 Spreadsheet + 6 分頁".
 * The header arrays are the source of truth for column layout in the
 * underlying Google Spreadsheet — `bootstrap.ts` writes them on first run
 * and repositories rely on them to map row arrays into typed structs.
 */

export const SPREADSHEET_NAME = 'Mochia Data';

/**
 * Pointer-only spreadsheet name used by joined users.
 *
 * iOS Safari's "Add to Home Screen" creates a standalone PWA with a
 * SEPARATE storage partition from regular Safari. A user who joined via
 * `?join=<id>` in Safari has the override in Safari's localStorage but
 * the PWA cold-load can't see it → bootstrap would create a fresh own-
 * Mochia Data and they'd lose access to the shared workbook.
 *
 * Workaround: when the override path runs, also create this tiny pointer
 * spreadsheet in the user's OWN Drive (drive.file scope sees it across
 * any device/context that the same Google account opens). Bootstrap
 * checks the pointer BEFORE creating own Mochia Data, so future cold
 * loads find the shared workbook automatically.
 */
export const POINTER_SPREADSHEET_NAME = 'Mochia Pointer';
/** Schema versions:
 *  - 1: pre-validate-sync-queue-payloads. Rows defaulted to rev=0.
 *  - 2: rows default to rev=1. Migration `bumpRevToOne` upgrades 1 → 2 by
 *    rewriting any rev=0 cells to rev=1 across all OCC-managed tabs.
 *  - 3: book-architecture-v2. Adds `books.type`, `transactions.tags`,
 *    `categories.book_id`. Migration `migrateToV3` backfills:
 *    - books missing `type` → 'personal'
 *    - custom categories (is_system != true) missing `book_id` → first
 *      non-deleted book's id
 *    - transactions with `project` non-empty AND `tags` empty → tags = '#' +
 *      project, project = ''
 *  - 4: cleanup pass. Detects books rows where `is_default` cell holds a
 *    type-code value ('personal'|'family'|'project') — these were seeded
 *    when the v3 schema briefly placed `type` in column 4 before being
 *    moved to the end. Realigns to current column order. Also force-
 *    tombstones any surviving `book-personal` row whose v3 cleanup missed.
 *  - 5: reorders the `transactions` tab (`tags` moved up after `note`,
 *    `time` dropped and folded into `date` as an optional `THH:MM` suffix).
 *  - 6: expands the category taxonomy. Migration `migrateToV6` backfills
 *    already-bootstrapped workbooks: appends the 3 new system subs
 *    (`sub-pet-supplement`, `sub-daily-clean`, `sub-sub-content`) if absent
 *    and renames `sub-sub-cloud` (雲端空間→雲端服務) / `sub-edu-online`
 *    (線上訂閱→課程訂閱) only when still at their original default names.
 */
export const SUPPORTED_SCHEMA_VERSION = 6;

export const TAB_NAMES = [
  'transactions',
  'books',
  'categories',
  'recurring',
  'notifications',
  'meta',
] as const;

export type TabName = (typeof TAB_NAMES)[number];

export interface TabSpec {
  readonly name: TabName;
  readonly headers: readonly string[];
}

export const TABS: { readonly [K in TabName]: TabSpec } = {
  // SHARP EDGE — schema columns must be APPEND-ONLY past v1.
  // Inserting a new column in the middle silently mis-aligns every existing
  // row (header is array-indexed; existing rows have N cells, header has N+1).
  // The v1.0 dogfood deploy hit this when `book_id`/`tags`/`type` were added
  // mid-array and rows started failing `parseInt10("true")`. Always append
  // new fields at the END so legacy rows just see empty trailing cells →
  // parsed as defaults.
  transactions: {
    name: 'transactions',
    // v5 EXCEPTION: this tab was reordered (tags moved up after note,
    // `time` dropped + folded into `date` as an optional `T HH:MM` suffix)
    // because the append-only churn from v3+v4 left `tags` and `time` at
    // the far right of the sheet — visually noisy when scanning the
    // workbook by hand. The v5 migration rewrites every existing row's
    // cells into the new positional layout, so older app versions WILL
    // mis-parse if they hit a v5 sheet — `checkSchemaVersion` blocks
    // that path. Future schema changes return to APPEND-ONLY.
    headers: [
      'id',
      'book_id',
      'date',
      'amount_minor',
      'currency',
      'kind',
      'category_id',
      'subcategory_id',
      'note',
      'tags',
      'project',
      'recurring_id',
      'rev',
      'created_at',
      'updated_at',
      'deleted_at',
    ],
  },
  books: {
    name: 'books',
    headers: [
      'id',
      'name',
      'color_token',
      'is_default',
      'rev',
      'created_at',
      'updated_at',
      'deleted_at',
      // v3 additions — appended:
      'type',
    ],
  },
  categories: {
    name: 'categories',
    headers: [
      'id',
      'parent_id',
      'name',
      'kind',
      'color_token',
      'icon',
      'sort_order',
      'is_system',
      'rev',
      'created_at',
      'updated_at',
      'deleted_at',
      // v3 additions — appended:
      'book_id',
    ],
  },
  recurring: {
    name: 'recurring',
    headers: [
      'id',
      'book_id',
      'title',
      'amount_minor',
      'currency',
      'kind',
      'category_id',
      'subcategory_id',
      'note',
      'freq',
      'freq_day',
      'start_date',
      'end_date',
      'last_generated_date',
      'active',
      'rev',
      'created_at',
      'updated_at',
      'deleted_at',
    ],
  },
  notifications: {
    name: 'notifications',
    headers: ['id', 'kind', 'payload', 'read_at', 'created_at'],
  },
  meta: {
    name: 'meta',
    headers: ['key', 'value'],
  },
};

/** Column letter for a header in a tab (A, B, ... Z, AA, AB, ...). */
export function columnLetter(index: number): string {
  if (index < 0) throw new Error(`column index out of range: ${index}`);
  let n = index;
  let s = '';
  while (n >= 0) {
    s = String.fromCharCode((n % 26) + 65) + s;
    n = Math.floor(n / 26) - 1;
  }
  return s;
}

/** Build an A1 range like "transactions!A2:O" (or with explicit row span). */
export function tabRange(tab: TabName, opts?: { startRow?: number; endRow?: number }): string {
  const headers = TABS[tab].headers;
  const lastCol = columnLetter(headers.length - 1);
  const startRow = opts?.startRow ?? 1;
  const endRow = opts?.endRow;
  return endRow == null
    ? `${tab}!A${startRow}:${lastCol}`
    : `${tab}!A${startRow}:${lastCol}${endRow}`;
}

/** Build an A1 range covering only the header row. */
export function headerRange(tab: TabName): string {
  return tabRange(tab, { startRow: 1, endRow: 1 });
}
